export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  image?: string;
  featured?: boolean;
}

export const blogPosts: BlogPost[] = [
  {
    slug: 'guest-reviews-published-website',
    title: 'Guest Reviews Now Published on Your Website',
    excerpt: 'Build trust with future guests by automatically displaying positive reviews on your accommodation pages — with full control over what gets published.',
    content: `
## Social Proof That Converts

Following our recent improvements to the satisfaction questionnaires, guest reviews are now automatically published on your i-Rent powered website. This means future visitors can see authentic feedback from previous guests.

## How It Works

### Automatic Publishing

Reviews intended for future tenants are automatically published on your website when **all ratings are 6 or higher**. These appear under the valuation section for each accommodation.

### Two Types of Reviews

Remember, guest comments are now divided into two categories:

1. **Reviews for the rental company** — Private feedback for your internal use
2. **Reviews for future tenants** — Public testimonials displayed on your website

### Full Control

You maintain complete control over what appears on your website. From your i-Rent account, navigate to **Satisfaction Questionnaires** where you can:

- Remove reviews you don't want published
- Publish additional reviews you'd like to share
- Manage both private and public comments

## Why Reviews Matter

- **Build trust** — Authentic guest feedback reassures potential bookers
- **Increase conversions** — Properties with reviews get more bookings
- **Improve SEO** — Fresh, user-generated content helps search rankings
- **Stand out** — Differentiate your properties from competitors

## Get Started

Reviews from your satisfied guests are now working for you 24/7, helping convince future visitors to book. Check your Satisfaction Questionnaires menu to manage your published reviews.
    `,
    author: 'i-Rent Team',
    date: '2013-03-10',
    readTime: '2 min',
    category: 'Guest Experience',
  },
  {
    slug: 'improved-satisfaction-questionnaires',
    title: 'Improved Guest Satisfaction Questionnaires',
    excerpt: 'Collect better feedback with our enhanced questionnaires — featuring separate comment fields and new questions about travel companions.',
    content: `
## Better Feedback, Better Service

After guests stay at one of your holiday accommodations, you can send them a questionnaire to learn about their experience. These results are valuable for improving your service and can be published on your i-Rent website as guest valuations.

We've made several improvements to make these questionnaires even more useful.

## What's New

### Two Separate Comment Fields

Guests now have two distinct areas for feedback:

1. **Private comments** — Feedback intended only for you, the rental company
2. **Public recommendations** — Comments meant to help future guests

This separation ensures you receive honest operational feedback while also collecting testimonials you can share publicly.

### New Travel Companion Question

We've added a question about who guests traveled with. This information helps you:

- Understand your guest demographics
- Tailor your marketing to your actual audience
- Identify which properties work best for different group types

![Guest Questionnaire Example](http://blog.i-rent.net/wp-content/uploads/2013/02/Questionaire-GB.jpg)

## Coming Soon

We're working on automatically publishing guest recommendations on your website, giving future visitors authentic testimonials from past guests.

## Benefits

- **Richer feedback** — Separate private and public comments
- **Better insights** — Understand who your guests are
- **Improved marketing** — Use real testimonials on your website
- **Higher satisfaction** — Act on private feedback to improve service

## Start Collecting Feedback

The improved questionnaire is now available. Send it to your guests after checkout and start gathering the insights you need to grow your business.
    `,
    author: 'i-Rent Team',
    date: '2013-02-15',
    readTime: '2 min',
    category: 'Guest Experience',
  },
  {
    slug: 'barcode-scanning-check-in',
    title: 'Speed Up Check-Ins with Barcode Scanning',
    excerpt: 'Introducing PDF417 barcode support on reservation forms — scan and retrieve booking details in under a second.',
    content: `
## Faster Check-Ins Are Here

We've added support for **PDF417 2D barcodes** to our reservation forms, dramatically speeding up the check-in process for your guests.

## How It Works

Each reservation form now includes a 2D barcode containing all the booking information. When a guest arrives, simply scan the barcode and the complete reservation opens instantly in i-Rent — no searching or scrolling through lists required.

![New reservation form with barcode](http://blog.i-rent.net/wp-content/uploads/2015/06/reservationform.png)

## Benefits

- **Lightning fast** — Retrieve reservation details in less than a second
- **No manual searching** — Skip scrolling through booking lists
- **Fewer errors** — Eliminate typos when looking up reservations
- **Professional experience** — Impress guests with efficient service

## What You Need

Getting started is simple:

1. **Request the upgrade** — Contact us to enable barcode support
2. **Get a 2D scanner** — Any modern scanner that reads PDF417 codes will work

![Motorola DS9208 Scanner](http://blog.i-rent.net/wp-content/uploads/2015/06/DS9208_LG_US-EN.jpg)

We tested extensively with the **Motorola DS9208** scanner, but compatible alternatives are widely available.

## Transform Your Check-In Process

Stop wasting time on manual lookups. With barcode scanning, your front desk operations become faster, smoother, and more professional. Contact us to get started!
    `,
    author: 'i-Rent Team',
    date: '2015-06-10',
    readTime: '2 min',
    category: 'Guest Experience',
  },
  {
    slug: 'new-reservation-form',
    title: 'Introducing the New Reservation Form',
    excerpt: 'A redesigned reservation form with improved layout, credit card authorization, and barcode scanner support for faster guest check-ins.',
    content: `
## A Better Booking Experience

We're always working to improve our services. One of our latest upgrades is a completely redesigned reservation form that makes the booking process clearer and more professional.

## What's New

### Improved Design

The new reservation form features a modern, clean layout that's easier for guests to read and complete.

![New Reservation Form](http://blog.i-rent.net/wp-content/uploads/2015/06/reservationform.png)

### Credit Card Authorization

A dedicated page for authorizing credit card payments has been added, streamlining the payment process and improving security.

### Barcode Scanner Support

The new form includes support for barcode scanners, enabling faster check-ins and reducing manual data entry errors.

## Key Benefits

- **Professional appearance** — Make a great impression with clean, modern documents
- **Clearer information** — Guests can easily understand booking details
- **Faster processing** — Barcode scanning speeds up check-in procedures
- **Secure payments** — Dedicated credit card authorization page

## Automatic Update

You don't need to do anything — the new reservation form is automatically used for all bookings.

## Important Note

To ensure your documents look their best, make sure your **bank data** is organized in two or three lines. You can update your bank details by going to the **Administrator** menu and selecting **Administrator Information**.

## Try It Out

The new reservation form is now active for all bookings. Your guests will enjoy a clearer, more professional booking experience.
    `,
    author: 'i-Rent Team',
    date: '2015-12-15',
    readTime: '2 min',
    category: 'Guest Experience',
  },
  {
    slug: 'customer-safety-https-ssl',
    title: 'Why Customer Safety Matters: HTTPS and SSL Explained',
    excerpt: 'Protect your guests\' personal data with HTTPS encryption — learn why SSL certificates are essential for your holiday rental website.',
    content: `
## Protecting Your Visitors Online

Online privacy is more important than ever. As we conduct more business through the internet, we leave behind increasing amounts of personal data. Unfortunately, this data can be stolen and misused by malicious parties.

For holiday rental websites, protecting visitor privacy isn't just good practice — it's essential for building trust with potential guests.

## What Is HTTPS?

**HTTPS** (Hyper Text Transfer Protocol Secure) is a secure version of HTTP, the protocol used for communication between browsers and web servers. When your website uses HTTPS, all data exchanged between your visitors and your server is encrypted and protected.

## Requirements for HTTPS

To properly implement HTTPS on your website, you need to ensure:

- **All internal links** point to HTTPS pages
- **301 redirects** are configured for any HTTP URLs
- **All external resources** (images, fonts, videos, iframes) load via HTTPS
- **No mixed content** — if even one element loads over HTTP, browsers will warn visitors that your site isn't secure

## SSL Certificates

To enable HTTPS on your website, you need an **SSL certificate**. This digital certificate authenticates your website's identity and enables the encrypted connection.

## Why This Matters for Your Business

- **Guest trust** — Visitors feel confident entering personal and payment information
- **Browser warnings** — Modern browsers mark HTTP sites as "Not Secure"
- **SEO benefits** — Google prioritizes secure websites in search rankings
- **Compliance** — Many regulations require encrypted data transmission

## We Can Help

i-Rent can customize your website and arrange the SSL certificate for you. Contact us to ensure your guests' data is protected with industry-standard encryption.
    `,
    author: 'i-Rent Team',
    date: '2017-04-20',
    readTime: '2 min',
    category: 'Guest Experience',
  },
  {
    slug: 'new-software-interface-redesign',
    title: 'Introducing the New i-Rent Software Interface',
    excerpt: 'Experience a completely redesigned booking system with improved filters, clearer data screens, and more intuitive calendar management.',
    content: `
## A Fresh Look for i-Rent

We're excited to unveil a completely redesigned software interface for the i-Rent booking system. This update brings significant improvements to how you manage your holiday rental business.

## What's New

### Improved List Views

All lists now feature enhanced filtering capabilities, making it easier to find exactly what you're looking for — whether it's bookings, accommodations, or guest information.

### Clearer Booking Screens

The booking data screens have been completely reorganized for clarity. You now have direct access to all booking information from a single, intuitive view.

### Enhanced Calendar Experience

The booking calendars are easier to navigate with more direct popup options. Manage your availability and reservations with fewer clicks.

### Restructured Accommodation Information

Property details have been reorganized and improved, making it simpler to update and maintain your accommodation listings.

## Countless Improvements

The enhancements to the system are too numerous to list individually. We invite you to explore the new interface yourself and discover all the ways we've made your daily workflow smoother.

## Try It Now

You can access the new interface immediately using your existing login credentials. Log in and experience the difference — we're confident you'll find it faster, cleaner, and more efficient than ever before.

We're working hard to continue refining and expanding these improvements. Thank you for being part of the i-Rent community!
    `,
    author: 'i-Rent Team',
    date: '2017-06-15',
    readTime: '2 min',
    category: 'Guest Experience',
  },
  {
    slug: 'premier-connectivity-partner-booking-com',
    title: 'i-Rent Is Now a Premier Connectivity Partner of Booking.com',
    excerpt: 'We\'re proud to announce our Premier Connectivity Partner status with Booking.com — enabling seamless integration with the world\'s largest accommodation platform.',
    content: `
## A Strategic Partnership

We're excited to announce that i-Rent has achieved **Premier Connectivity Partner** status with Booking.com. This recognition reflects our commitment to providing the highest quality integration with the world's leading accommodation booking platform.

![Booking.com website](http://blog.i-rent.net/wp-content/uploads/2018/03/booking.com-website-e1519946643296.png)

## Why This Matters

As a Premier Partner, we work closely with Booking.com to ensure your properties are published in the most effective way possible. This collaboration means:

- **Optimized listings** — Your accommodations are presented to maximum effect
- **Reliable synchronization** — Real-time updates for availability and pricing
- **Priority support** — Direct access to technical resources
- **Latest features** — Early access to new Booking.com capabilities

## Booking.com by the Numbers

The scale of Booking.com is unmatched in the travel industry:

- **43 languages** supported on the platform
- **198+ offices** worldwide
- **120,000+ destinations** globally
- **1,550,000+ room nights** booked every single day

![Premier Partner Booking.com](http://blog.i-rent.net/wp-content/uploads/2018/03/premier-prtner-e1519947514649.png)

## What This Means for You

With i-Rent's Premier Partner status, you benefit from a direct, reliable connection to the world's largest booking platform. Your listings reach millions of travelers daily, with the confidence that your availability and pricing are always accurate.

## Get Connected

If you're not yet publishing to Booking.com through i-Rent, now is the perfect time to start. Contact us to learn how our Premier Partner integration can help grow your bookings.
    `,
    author: 'i-Rent Team',
    date: '2018-03-01',
    readTime: '2 min',
    category: 'Marketing',
  },
  {
    slug: 'gdpr-compliance-holiday-rentals',
    title: 'GDPR Compliance for Holiday Rental Websites',
    excerpt: 'Ensure your holiday rental website meets GDPR requirements with professionally prepared legal documents and consent tools — available in 10+ languages.',
    content: `
## Understanding GDPR Requirements

The General Data Protection Regulation (GDPR) established strict requirements for protecting the privacy of EU residents. Any company that collects or processes data on European customers must comply with these regulations.

## What Your Website Needs

To be GDPR compliant, your website must clearly communicate:

- **What information is collected** — Personal data, cookies, and tracking
- **How information is used** — Booking processing, marketing, analytics
- **Who data is shared with** — Third parties, payment processors, OTAs
- **How long data is retained** — Storage periods and deletion policies
- **User rights** — Access, correction, deletion, and data portability

## How i-Rent Helps

We've prepared comprehensive legal documents specifically designed for holiday rental businesses:

### Ready-to-Use Documents

- **Terms of Use** — Clear conditions for website visitors
- **Cookie Policy** — Transparent disclosure of tracking technologies
- **Privacy Policy** — Complete data protection information

### Professional Quality

All documents have been carefully prepared by industry professionals who understand the unique requirements of holiday rental operations.

### Multilingual Support

Documents are available in **more than 10 languages**, ensuring your guests can read policies in their native language.

### Consent Management

We've also prepared the necessary tools to obtain proper user consent according to GDPR requirements — including cookie banners and preference management.

## Get Compliant Today

Don't risk non-compliance. Contact us and we'll take care of updating your website with all the necessary legal documents and consent mechanisms.
    `,
    author: 'i-Rent Team',
    date: '2018-05-10',
    readTime: '2 min',
    category: 'Operations',
  },
  {
    slug: 'updated-photo-system',
    title: 'Introducing the Updated Photo System',
    excerpt: 'Upload photos up to 10 megapixels, enjoy faster management tools, and use multilingual photo tags to improve your listings across all platforms.',
    content: `
## A Complete Photo System Overhaul

We're excited to announce the general availability of our completely redesigned photo system. These new tools make managing your accommodation photos faster, more flexible, and more powerful than ever.

## What's New

### High-Resolution Support

You can now upload photos up to **10 megapixels** — showcasing your accommodations with fantastic image quality on:

- Your own website
- Booking.com
- Airbnb
- HomeAway
- And other connected platforms

### Faster Photo Management

The new interface is built for speed. Uploading, organizing, and editing your photo galleries is now significantly quicker, saving you valuable time when updating your listings.

### Multilingual Photo Tags

Improve both user experience and SEO by adding descriptive tags to your photos. Tags are available in **more than 10 languages**, helping international guests understand exactly what each image shows.

## Benefits

- **Better first impressions** — High-resolution photos attract more clicks and bookings
- **Improved SEO** — Properly tagged images help search engines index your website
- **Consistent quality** — Your photos look professional across all platforms
- **Time savings** — Faster tools mean less time managing, more time growing your business

## Try It Today

The updated photo system is now available in your i-Rent dashboard. Start uploading high-resolution images and adding multilingual tags to take your listings to the next level.
    `,
    author: 'i-Rent Team',
    date: '2020-01-20',
    readTime: '2 min',
    category: 'Technology',
  },
  {
    slug: 'high-resolution-photos-portals',
    title: 'High-Resolution Photos and Tags Now Sent to OTAs',
    excerpt: 'Your property photos are now published in high resolution with descriptive tags to Booking.com, Airbnb, and other major portals.',
    content: `
## Better Photos on Every Platform

We've upgraded how i-Rent sends photos to external portals. Your high-resolution images are now delivered in full quality, along with descriptive tags that help platforms categorize and display your photos effectively.

## Photo Tags by Platform

Different portals support photo tagging in different ways:

- **Booking.com** — Supports multiple tags per photo
- **Airbnb** — Supports one tag per photo
- **HomeAway** — Supports one tag per photo with multiple language translations

## Why Photo Tags Matter

Photo tags help booking platforms understand what each image shows — whether it's a bedroom, bathroom, pool, or view. This improves:

- **Search visibility** — Platforms can match your photos to guest searches
- **Guest experience** — Travelers find what they're looking for faster
- **Listing quality** — Well-tagged photos signal a professional listing

## High Resolution Everywhere

Along with tags, your photos are now sent in high resolution to portals that support it. This means:

- **Sharper images** — No more compression artifacts
- **Better first impressions** — High-quality photos attract more clicks
- **Professional presentation** — Your properties look their best

## Get Started

If you haven't tried our updated photo system yet, now is the perfect time. Take advantage of high-resolution uploads and photo tagging to make your listings stand out on every platform.
    `,
    author: 'i-Rent Team',
    date: '2020-02-10',
    readTime: '2 min',
    category: 'Operations',
  },
  {
    slug: 'discounts-sync-airbnb',
    title: 'Simple Discounts Now Sync to Airbnb',
    excerpt: 'Your percentage-based discounts created in i-Rent are now automatically sent to Airbnb — helping you attract more bookings with consistent promotions.',
    content: `
## Discounts on Airbnb, Managed from i-Rent

We've expanded our Airbnb integration to include discount synchronization. Simple discounts you create in i-Rent are now automatically sent to your Airbnb listings.

## Requirements for Airbnb Discounts

For a discount to sync to Airbnb, it must meet these conditions:

- **Percentage-based** — Only discounts using a percentage (%) are supported
- **No maximum stay** — The discount cannot have a maximum stay restriction

## How Discounts Are Applied

When sending discounts to Airbnb, i-Rent applies the following rules:

### Date Settings
- **Publishing begin/end dates** — If set, the discount only appears during that period
- **Arrival date range** — If specified, the discount applies to bookings arriving within those dates; if left empty, the discount applies to all days with pricing

### Stay Requirements
- **Minimum stay** — If you set a minimum stay, the discount only applies when it's equal to or less than the accommodation's minimum stay requirement
- **Maximum stay** — Not supported for Airbnb discounts

### Calculation Method
- **Applying criteria** — The discount applies if at least one day of the booking falls within the discount period
- **Calculation** — The discount is calculated only for days that fall within the discount period

## Benefits

- **Consistent promotions** — Manage all your discounts from one place
- **Automatic sync** — No need to manually update Airbnb
- **More bookings** — Attractive discounts help fill your calendar
- **Full control** — Set exactly when and how discounts apply

## Start Creating Discounts

Create your percentage-based discounts in i-Rent and watch them sync automatically to Airbnb. It's a simple way to boost your visibility and attract more guests.
    `,
    author: 'i-Rent Team',
    date: '2020-02-25',
    readTime: '2 min',
    category: 'Operations',
  },
  {
    slug: 'accommodation-categories-rental-types',
    title: 'Categorize Accommodations by Rental Type',
    excerpt: 'Organize your portfolio by distinguishing between holiday rentals, long-term rentals, and maintenance-only properties — streamlining your publishing workflow.',
    content: `
## Better Organization for Your Properties

We've added a new feature that helps you organize your accommodations more effectively by categorizing them based on their purpose.

## Three Property Categories

You can now assign each accommodation to one of three categories:

- **Holiday rentals** — Properties available for short-term holiday bookings
- **Long-term rentals** — Properties intended for extended stays or residential leases
- **Maintenance and service only** — Properties not available for guest bookings

## How to Set Up

Navigate to the **Accommodation Information** menu in i-Rent and select the appropriate category for each property. The setting is quick to configure and takes effect immediately.

## Smarter Publishing

The real benefit comes when publishing to websites and portals:

- **Maintenance-only properties are automatically excluded** from publishing lists
- **No more accidental listings** of properties that shouldn't be bookable
- **Bulk publishing becomes safer** — select groups of accommodations without worrying about including the wrong properties

## Benefits

- **Cleaner workflow** — Focus only on properties that should be published
- **Fewer errors** — Prevent maintenance properties from appearing on booking sites
- **Better organization** — Clear separation between different rental types
- **Time savings** — No need to manually exclude properties when publishing

## Start Organizing

Take a few minutes to categorize your accommodations. This small investment of time will make your day-to-day operations smoother and reduce the risk of publishing mistakes.
    `,
    author: 'i-Rent Team',
    date: '2020-03-10',
    readTime: '2 min',
    category: 'Operations',
  },
  {
    slug: 'holidu-online-payment-link',
    title: 'Online Payment Links Now Displayed on Holidu',
    excerpt: 'Holidu bookings now include automatic payment links — guests see the payment option directly on the confirmation page and in their email.',
    content: `
## Seamless Payments for Holidu Bookings

We've integrated automatic payment links for bookings made through Holidu. This streamlines the payment process and improves the guest experience.

## How It Works

When Holidu creates a booking, i-Rent automatically sends back an online payment link. This link is then:

- **Displayed on Holidu's booking confirmation page** — guests can pay immediately after booking
- **Included in the confirmation email** — easy access for guests who prefer to pay later

The payment link includes the rental amount plus any mandatory extras that need to be paid at the time of booking.

![Holidu website booking confirmation page](http://blog.i-rent.net/wp-content/uploads/2020/07/holidu-payment-link-1-1024x522.jpg)

![Holidu booking confirmation email](http://blog.i-rent.net/wp-content/uploads/2020/07/holidu-payment-link-2-1024x506.jpg)

## Requirements

This feature is available for property managers who have a **POS (Point of Sale) system** configured in i-Rent. If you haven't set up your payment system yet, contact our support team for assistance.

## Benefits

- **Faster payments** — Guests can pay immediately without waiting for manual invoices
- **Better conversion** — Reducing friction leads to more completed bookings
- **Professional experience** — Branded payment pages build trust with guests
- **Automatic tracking** — All payments sync directly to your i-Rent dashboard

## Get Started

If you're already connected to Holidu with a POS system in i-Rent, this feature is active automatically. Start enjoying smoother payment flows for your Holidu bookings today.
    `,
    author: 'i-Rent Team',
    date: '2020-07-15',
    readTime: '2 min',
    category: 'Operations',
  },
  {
    slug: 'better-photos-holiday-homes',
    title: 'How to Take Better Pictures of Holiday Homes',
    excerpt: 'Professional photography tips for holiday rentals — from exterior shots to interior staging, learn how to capture images that convert browsers into bookers.',
    content: `
## Why Great Photos Matter

Quality photography is one of the most important factors in attracting bookings. These professional tips will help you capture images that showcase your properties at their best.

## Camera Basics

Before you start shooting, keep these fundamentals in mind:

- **Always shoot in landscape format** to avoid cropping issues on OTAs and portals
- **Use maximum resolution** for the sharpest possible images
- **Keep the camera level** (about 1.5m from the ground) to avoid skewed walls and doors
- **Use a wide-angle lens** to capture more of each space
- **Take day shots plus 1-2 evening shots** just after sunset when the sky is still blue

## Outdoor Photography

### General Guidelines

1. **Only shoot on sunny days** — small white clouds are acceptable
2. **Position the sun behind you** for blue skies and even lighting
3. **Open all blinds, doors, and curtains** visible from outside
4. **Minimize neighboring buildings** in your frame — avoid showing windows of surrounding houses

### Garden & Pool

- Prepare the area exactly as guests would find it on arrival
- Arrange sun loungers and furniture in an orderly, inviting way
- Add towels on loungers and use tasteful decoration
- Hide garden hoses and messy items
- **Pro tip:** Stir the pool water with a broom before shooting to hide any debris on the bottom

### Terraces

- Keep everything clean and tidy
- Set the table completely with plates, cutlery, wine, and fruit
- Create an atmosphere that invites dining

### Views

- Minimize surrounding buildings in the frame
- Shoot with the sun behind you
- Show that the view belongs to this specific property

## Indoor Photography

### General Indoor Tips

- **Turn on ALL lights** — every single one
- **Tidy thoroughly** and remove clutter
- **Shoot from corners** with no more than 2 walls visible — this creates better spatial feeling
- **Rearrange furniture** to create more space; avoid photographing backs of sofas
- **Hide cables** and electrical wires along walls
- **Straighten cushions** or remove them for cleaner compositions
- Use decoration, but don't overdo it

### Managing Light

- If too much sunlight enters through windows, close curtains or cover windows from outside
- For shots through windows to a bright exterior, use a tripod and take two exposures — one for the view, one for the interior — then combine in Photoshop

### Living & Dining Rooms

- Remove carpets, sofa covers, and excess cushions
- Remove personal items like family photos
- Open windows and doors when light permits
- Capture interesting detail shots

### Bedrooms

- Use only white sheets or duvets
- Add rolled towels on the bed and multiple pillows
- Remove all personal items
- In small rooms, focus on the bed detail with nightstands

### Bathrooms

- Remove all personal items
- If space is too tight for landscape orientation, take two portrait shots and combine them side by side

## Post-Processing Tips

- Crop away any distracting elements
- Straighten walls and doors in your photo editor
- Brighten images and sharpen where necessary
- Ensure consistent quality across all photos

## Start Shooting

Great photography doesn't require expensive equipment — just attention to detail and proper technique. Apply these tips consistently and watch your booking inquiries increase.
    `,
    author: 'i-Rent Team',
    date: '2021-04-15',
    readTime: '5 min',
    category: 'Revenue Management',
  },
  {
    slug: 'extra-packages-ota-bookings',
    title: 'New Possibilities to Charge Extra Services',
    excerpt: 'Bundle multiple extras into a single package for OTA bookings — ensuring accurate pricing while maintaining full transparency on your own website.',
    content: `
## Improved Extras Tool in i-Rent

We've enhanced the extras functionality in i-Rent with powerful new features designed to solve a common industry challenge.

## The Problem

OTA platforms offer limited options for calculating extras in online bookings. Typically, extras that OTAs cannot handle are simply added as text in the accommodation description. The result? Guests often overlook this information, leading to disputes and uncomfortable discussions at check-in.

## The Solution: Extra Packages

You can now bundle multiple extras into a single **extra package**.

Here's how it works:

1. **Create an extra package** combining several individual extras
2. **i-Rent sends this package as one item** to OTAs that support it
3. **The OTA includes it in the booking calculation** — no hidden surprises
4. **When the booking returns to i-Rent**, the extras are automatically split back into separate line items

## How It Works on Your Website

On your own website, extra packages behave differently:

- Individual extras are displayed separately for full transparency
- You can choose to **include certain items invisibly** in the rental price
- Or **show them as separate line items** in the price breakdown

This flexibility allows you to handle items like administration fees, bed linen, or mid-stay cleaning without raising questions from guests.

## Common Use Cases

- **Administration costs** — Include in the base price seamlessly
- **Bed linen and towels** — Bundle as a mandatory package
- **Cleaning during stay** — Add to longer bookings automatically
- **Welcome packages** — Combine multiple items into one extra

## Get Started

To discover the full potential of extra packages for your properties, contact our helpdesk for personalized assistance. We'll help you set up a configuration that works best for your business.
    `,
    author: 'i-Rent Team',
    date: '2021-04-28',
    readTime: '2 min',
    category: 'Revenue Management',
  },
  {
    slug: 'flexible-pricing-ota-partners',
    title: 'Flexible Price Adjustments for OTAs and Agents',
    excerpt: 'Take control of your revenue strategy with flexible price adjustments — set custom markups per partner, per property, and per period directly in i-Rent.',
    content: `
## A New Tool for Smarter Pricing

We've added a powerful new feature to i-Rent that gives you complete control over how prices are adjusted for your rental partners, including OTAs and agents.

You'll find this tool under **Administrator → External Price Settings**.

## What Can You Do?

With this feature, you can adjust prices:

- **Per rental partner** — Apply different markups for Booking.com, Airbnb, agents, etc.
- **Per accommodation** — Set specific adjustments for individual properties or groups
- **Per time period** — Increase prices during peak seasons or reduce them in low season

## Step-by-Step Setup

### Step 1: Define Your Price Adjustment

Choose a name for the adjustment, set the percentage increase (or decrease), and select the date range.

![Step 1: Define price adjustment](https://blog.i-rent.net/wp-content/uploads/2021/05/summer2021.jpg)

### Step 2: Select Accommodations

Choose which properties this adjustment applies to — you can select all or pick specific ones.

![Step 2: Select accommodations](https://blog.i-rent.net/wp-content/uploads/2021/05/summer2021-2.jpg)

### Step 3: Select Partners

Choose which rental partners should receive this price adjustment.

![Step 3: Select partners](https://blog.i-rent.net/wp-content/uploads/2021/05/summer2021-2-2.jpg)

## Unlimited Flexibility

You can create as many different price adjustments as you need:

- Different markups for different partner groups
- Seasonal variations across your portfolio
- Property-specific strategies for high-demand accommodations

**Pro tip:** You can also set negative percentages to reduce prices — perfect for offering special rates to preferred partners or during slower periods.

## Start Optimizing Your Revenue

This feature puts you in control of your pricing strategy across all channels. Set it up once and let i-Rent handle the rest automatically.
    `,
    author: 'i-Rent Team',
    date: '2021-05-15',
    readTime: '2 min',
    category: 'Revenue Management',
  },
  {
    slug: 'marriott-homes-villas-channel-manager',
    title: 'New Channel: Homes & Villas by Marriott International',
    excerpt: 'Reach premium travelers worldwide through Marriott\'s curated holiday rental platform — now available via i-Rent\'s Channel Manager.',
    content: `
## Introducing Homes & Villas by Marriott International

[Homes & Villas](https://homes-and-villas.marriott.com/) is Marriott International's global holiday rental offering, launched in 2019. With over 90 years of hospitality expertise, Marriott has brought its legendary service standards to the holiday rental market.

![Homes & Villas by Marriott](https://blog.i-rent.net/wp-content/uploads/2021/05/homes-and-villas.png)

## A Focus on Premium Properties

Unlike mass-market booking platforms, Homes & Villas focuses exclusively on **premium and luxury rental properties**. Marriott carefully selects professional property managers as partners, ensuring that every listed property meets the high standards guests expect from the Marriott name.

## Why This Matters for Your Business

### Access to Marriott's Global Audience
With Marriott's vast customer base of loyal travelers, listing on Homes & Villas gives your premium properties exposure to millions of potential guests who value quality and reliability.

### Professional Standards
Being part of the Marriott network signals to guests that your property has been vetted and meets international hospitality standards — building instant trust.

### Seamless Integration
Through i-Rent's Channel Manager, you can now connect your listings to Homes & Villas with full synchronization of availability, pricing, and bookings.

## How to Get Started

1. **Ensure your properties meet premium standards** — Marriott focuses on high-quality rentals
2. **Contact i-Rent support** to express your interest in the Homes & Villas channel
3. **Complete the partner application** with Marriott
4. **Start receiving bookings** synced directly to your i-Rent dashboard

## Elevate Your Portfolio

If you manage premium holiday rentals, Homes & Villas by Marriott International offers an excellent opportunity to reach discerning travelers who expect nothing but the best. Contact us to learn more about connecting your properties to this prestigious platform.
    `,
    author: 'i-Rent Team',
    date: '2021-05-20',
    readTime: '2 min',
    category: 'Marketing',
  },
  {
    slug: 'bed-sizes-accommodation-description',
    title: 'Add Bed Sizes to Your Accommodation Description',
    excerpt: 'Bed sizes matter to guests — now you can include them in your property descriptions automatically, reducing questions and helping travelers book with confidence.',
    content: `
## Why Bed Sizes Matter

Bed sizes are often a deciding factor for guests when choosing their accommodation. From couples seeking a king-size bed to families needing specific arrangements for children, this information directly influences booking decisions.

By including bed dimensions in your property descriptions, you reduce guest inquiries, avoid misunderstandings, and create a more transparent booking experience.

## How to Add Bed Sizes in i-Rent

Follow these simple steps to include bed sizes in your accommodation descriptions:

1. **Open the accommodation info sheet** in i-Rent.net
2. Navigate to the **floor section** where bedrooms are described
3. Look for the option to **add or edit beds**
4. Select the appropriate bed types and sizes from the available options
5. **Save the info sheet**
6. Go to the **accommodation description** and regenerate it to include the updated bed information

## Important Note

When regenerating the accommodation description, any **manual text changes** you previously made will be overwritten.

If you've customized the description text, be sure to **copy and save those changes** before regenerating — you can add them back afterwards.

## Benefits of Including Bed Sizes

- **Fewer guest questions** — Provide the information travelers need upfront
- **Faster booking decisions** — Help guests confirm the property meets their needs
- **Reduced complaints** — Set clear expectations before arrival
- **Professional presentation** — Show attention to detail in your listings

## Start Updating Your Listings

Take a few minutes to add bed sizes to your property info sheets. This small improvement can make a big difference in guest satisfaction and booking conversions.
    `,
    author: 'i-Rent Team',
    date: '2022-01-12',
    readTime: '2 min',
    category: 'Guest Experience',
  },
  {
    slug: 'price-adjustment-calculation-otas',
    title: 'Improved Price Adjustment Calculation for OTAs and Agents',
    excerpt: 'We\'ve updated how price markups are calculated — ensuring your base rental amount is preserved accurately when applying percentage increases for channels.',
    content: `
## A Smarter Way to Calculate Price Markups

We've made an important change to how price increases are calculated when publishing to OTAs and agents. This update ensures your pricing remains accurate and predictable.

## The Problem with the Old Method

Previously, price markups were calculated by adding a percentage to the base price:

**Example:** Base rental price €900 + 10% (€90) = OTA price €990

The issue? When the OTA deducts their 10% commission from €990, you receive €891 — not your original €900 base price. This created a gap in your expected revenue.

## The New Calculation Method

The updated calculation works backwards from the selling price, ensuring that after the OTA deducts their percentage, you receive exactly your base rental amount.

In other words: if you set a 10% markup and the OTA takes 10%, your net result is now precisely €900 — as intended.

## Visual Updates in the Booking Page

To provide full transparency, we've updated the booking interface:

- Under the **Price tab**, the markup amount is now displayed in blue
- You can always see your base rental amount and the added increase separately
- A new column in the **General Booking Overview** shows these amounts at a glance
- An explanatory message appears in the green info field on the booking page

![Price adjustment illustration](https://blog.i-rent.net/wp-content/uploads/2022/03/incremento-de-precio-1024x626.jpg)

## Protect Your Margins

This update helps you maintain healthy margins when working with OTAs and agents. No more unexpected revenue gaps — just accurate, predictable pricing across all your channels.
    `,
    author: 'i-Rent Team',
    date: '2022-03-18',
    readTime: '2 min',
    category: 'Technology',
  },
  {
    slug: 'owner-smartphone-app',
    title: 'Introducing the i-Rent Owner App for Property Owners',
    excerpt: 'Empower your property owners with a dedicated smartphone app — manage calendars, view income, share documents, and communicate directly with your team.',
    content: `
## A New Way to Connect with Property Owners

In addition to the web-based login that owners already use within i-Rent, we're excited to introduce a dedicated **smartphone app** designed specifically for property owners.

![Owner App preview](http://blog.i-rent.net/wp-content/uploads/2022/04/ownersApp.png)

## Why an Owner App?

Communication between property owners and managers is crucial for a well-organized rental season. With a mobile app on their smartphone, owners can connect directly with their property manager — speeding up information flow and making data more transparent. The result? Stronger owner relationships and smoother operations.

## What Can Owners Do with the App?

### Calendar Management
- Update the calendar with private bookings for personal use
- Block periods when the property is unavailable
- Submit service requests for private stays
- Add guest details for personal bookings

### Occupancy Overview
- View a complete list of all bookings
- Update or cancel owner bookings
- See all arrival and departure dates at a glance

### Discounts
- Post discounts (with manager permission)
- Set percentage-based discounts between specific dates
- Discounts are deducted from the owner's share of rent
- Changes appear immediately on your website

### Revenue Tracking
- Access net rental income statements
- View income organized by season
- Sort and filter as needed

### Document Sharing
- Share documents and photos with the property manager
- Receive files directly on the smartphone
- Forward documents via email from the app

### Messaging
- Send and receive messages with the property manager
- Get notifications for updates and announcements
- All messages sync to the manager's i-Rent dashboard

![Owner App features](https://blog.i-rent.net/wp-content/uploads/2023/03/OwenersAppBlog-1024x526.jpg)

## Available on iOS and Android

The Owner App is available for both **Apple** and **Android** smartphones, and supports **7 languages**: English, Spanish, German, French, Dutch, Italian, and Portuguese.

**Download now:**
- [App Store (iOS)](https://apps.apple.com/app/i-rent-net-owners-app/id1569601853?platform=iphone)
- [Google Play (Android)](https://play.google.com/store/apps/details?id=net.irent.ownerapp&hl=es_AR&gl=US)

## Strengthen Your Owner Relationships

Give your property owners the tools they need to stay informed and engaged. The i-Rent Owner App makes collaboration easier than ever.
    `,
    author: 'i-Rent Team',
    date: '2022-04-15',
    readTime: '3 min',
    category: 'Technology',
  },
  {
    slug: 'ical-export-made-easy',
    title: 'Creating and Exporting iCal Is Now Easier Than Ever',
    excerpt: 'Sync your i-Rent calendar with any platform using iCal export — generate your calendar link in just a few clicks.',
    content: `
## Seamless Calendar Synchronization

The iCal export feature in i-Rent allows you to effortlessly connect your availability calendar with other platforms and websites. Whether you're listing on a niche booking site or sharing availability with partners, iCal provides a universal solution.

With just a few simple steps, you can generate an iCal link directly from i-Rent and share it wherever you need.

## How to Create an iCal Export

Follow these steps to export your calendar:

1. In i-Rent, navigate to the **Accommodations** menu and select **List accommodations**
2. Right-click on the property you want to export
3. Go to **Channel Manager** → **Export iCal**
4. Click **Activate iCal** — and you're done!

![iCal export menu](https://blog.i-rent.net/wp-content/uploads/2022/06/screenshot-www.rentalbookingsystem.com-2022.06.29-10_47_15-1024x500.png)

![iCal activation screen](https://blog.i-rent.net/wp-content/uploads/2022/06/screenshot-www.rentalbookingsystem.com-2022.06.29-10_51_06.png)

## Why Use iCal Export?

- **Universal compatibility** — iCal works with virtually any calendar system
- **Real-time updates** — External platforms automatically pull your latest availability
- **Prevent double bookings** — Keep all your calendars synchronized
- **Flexible publishing** — Connect to platforms without direct API integration

## Start Syncing Today

If you're working with platforms that don't have a direct connection to i-Rent, iCal export is the perfect solution. Set it up once and enjoy automatic calendar synchronization across all your channels.
    `,
    author: 'i-Rent Team',
    date: '2022-06-29',
    readTime: '2 min',
    category: 'Technology',
  },
  {
    slug: 'plum-guide-channel-integration',
    title: 'New Channel: Publish Your Properties on Plum Guide',
    excerpt: 'Reach quality-conscious travelers through Plum Guide — a curated platform featuring only the best holiday rentals, now available via i-Rent\'s Channel Manager.',
    content: `
## What Is Plum Guide?

![Plum Guide](https://blog.i-rent.net/wp-content/uploads/2023/01/pluimGuide-for-website.png)

Founded in London in 2016, [Plum Guide](https://www.plumguide.com) has rapidly expanded to offer a curated selection of high-quality holiday rentals across hundreds of destinations in Europe, the UK, and the United States.

Unlike other platforms that list millions of properties, Plum Guide uses a rigorous selection process known as the **Plum Guide Award** to feature only the best homes. This makes it easier for discerning travelers to find and book exceptional accommodations.

## Why List on Plum Guide?

### Premium Positioning
With over 15 million holiday homes listed online across various platforms, standing out can be challenging. Plum Guide's curated approach means your property is presented alongside only the highest-quality rentals — attracting guests who value quality over price.

### Automatic Synchronization
Through a partnership with Bookingpal and i-Rent.net, you can easily publish your listings on Plum Guide with **automatic booking synchronization**. Reservations flow directly into your i-Rent account, eliminating manual work and reducing the risk of double bookings.

### Dedicated Support
Our support team is ready to guide you through the entire process — from initial setup to managing incoming bookings.

## How to Get Started

1. **Contact i-Rent support** to express your interest in Plum Guide
2. **Apply for Plum Guide** through [their partner application page](https://discover.plumguide.com/api-apply-page/?source=IRent)
3. **Complete the setup** with assistance from our team
4. **Start receiving bookings** automatically synced to your i-Rent dashboard

## Elevate Your Rental Business

If your properties meet Plum Guide's quality standards, this channel offers an excellent opportunity to reach a premium audience and increase your booking revenue. Contact us today to learn more!
    `,
    author: 'i-Rent Team',
    date: '2023-01-25',
    readTime: '2 min',
    category: 'Marketing',
  },
  {
    slug: 'akomgo-benefits-property-owners',
    title: 'Why You Should Publish Your Properties on Akomgo.com',
    excerpt: 'Discover how Akomgo.com — created by i-Rent.net — can boost your property visibility through Google Travel integration and a network of local estate agents.',
    content: `
## What Is Akomgo.com?

[Akomgo.com](https://www.akomgo.com) is an online holiday rental platform created by i-Rent.net in 2015. It connects property owners and managers with travelers searching for holiday homes and other accommodations worldwide.

But what makes Akomgo.com stand out from other booking platforms?

## Key Benefits for Property Managers

### Maximum Visibility

When you publish your properties on Akomgo.com, they automatically appear on **Google Travel** — dramatically increasing your exposure to potential guests who search for accommodations through Google.

Additionally, Akomgo.com partners with local estate agents who receive an embedded script for their websites containing all Akomgo.com listings. This network effect means your properties gain visibility across multiple channels simultaneously.

### Simple Publishing Process

Akomgo.com is committed to making the listing process as straightforward as possible. Whether your properties are available for instant booking or on-request only, you can easily publish them on the platform.

### Continuous Innovation

The platform is continuously improved with the latest technology and marketing strategies. Akomgo.com stays ahead of industry trends to ensure your listings reach the widest possible audience.

## How to Get Started

Publishing your properties on Akomgo.com is simple:

1. Log in to your i-Rent dashboard
2. Navigate to **Channel Manager → Publications**
3. Select **Akomgo.com** as your destination channel
4. Your accommodations will appear at [akomgo.com/holiday-homes](https://www.akomgo.com/holiday-homes)

## Grow Your Reach Today

Don't miss the opportunity to connect with a global audience and expand your rental business. With Akomgo.com's Google Travel integration and partner network, your properties get the visibility they deserve.

Start publishing today and watch your bookings grow!
    `,
    author: 'i-Rent Team',
    date: '2023-03-20',
    readTime: '2 min',
    category: 'Marketing',
  },
  {
    slug: 'leisure-time-ota-integration',
    title: 'New Channel: Connect to Leisure Time via i-Rent',
    excerpt: 'Discover Leisure Time — a next-level booking platform with fair commissions, quality-focused partnerships, and a no-cure, no-pay approach.',
    content: `
## Introducing Leisure Time

Looking for a booking platform that showcases your holiday rentals to the right audience? **Leisure Time** offers a fresh, innovative approach to connecting property managers with quality-conscious travelers.

With transparent communication, favorable conditions, and a commitment to mutual success, Leisure Time is designed to benefit both you and your guests.

![Leisure Time platform](https://blog.i-rent.net/wp-content/uploads/2023/04/Afbeelding-i-rent-2-1-1024x724.png)

## Why Choose Leisure Time?

### Fair Commission Model
Leisure Time only charges commission on the rental amount — excluding additional costs like housekeeping, bed linen, and tourist taxes. This is one of the most attractive commission structures in the industry.

### Quality-Focused Partnership
They prioritize sustainable, long-term relationships with partners who share their commitment to exceptional guest experiences. It's not just about bookings — it's about building something together.

### No-Cure, No-Pay
You only pay when you earn. This risk-free approach means Leisure Time is fully invested in your success.

### Stay Ahead of Trends
Let Leisure Time handle the online booking experience while you focus on delivering unforgettable stays. They continuously innovate to keep up with the latest developments in holiday rental marketing.

## How to Get Started

Ready to expand your reach with Leisure Time? Here's how:

1. Visit [leisuretime.com/en/leisure-time-business](https://leisuretime.com/en/leisure-time-business) to learn more
2. Contact their team at **partners@leisuretime.com**
3. Once registered, reach out to i-Rent support to activate the channel connection

## Grow Your Business

Join a platform that values quality, transparency, and long-term success. With Leisure Time integrated into i-Rent, you can manage this new channel alongside all your other OTAs from one central dashboard.
    `,
    author: 'i-Rent Team',
    date: '2023-04-05',
    readTime: '3 min',
    category: 'Marketing',
  },
  {
    slug: 'global-settings-otas',
    title: 'Global Settings: Configure All Your OTA Listings at Once',
    excerpt: 'Save time and ensure consistency across Booking.com, Airbnb, VRBO, and Holidu by managing shared settings for all your properties in one place.',
    content: `
## Simplify Multi-Property Management

Managing holiday rentals across multiple platforms can quickly become overwhelming — especially when you need to update policies, check-in times, or payment settings for dozens of properties. That's why i-Rent offers **Global Settings**, a powerful feature designed to streamline your workflow.

## What Are Global Settings?

Global Settings allow you to define general information that applies to all your properties listed on a specific OTA. Instead of configuring each listing individually, you set your preferences once and they apply everywhere.

This feature is available for leading holiday rental portals including:

- **Booking.com**
- **Airbnb**
- **VRBO**
- **Holidu**

## What Can You Configure?

With Global Settings, you can manage essential parameters such as:

- **Arrival and departure times** — Set standard check-in and check-out hours
- **Cancellation policies** — Define your terms across all listings
- **Payment methods** — Specify accepted payment options
- **Minimum age requirements** — Set guest age restrictions
- **Payment calendars** — Configure when payments are due
- **And more** — Additional settings specific to each platform

## Key Benefits

- **Consistency** — Ensure all your listings have the same policies and information
- **Time savings** — Update once, apply everywhere
- **Fewer errors** — Reduce the risk of inconsistent or outdated information
- **Easier scaling** — Add new properties without repetitive setup work

## Get Started Today

Stop wasting time on repetitive manual updates. Use i-Rent's Global Settings feature to manage your OTA listings more efficiently and focus on what matters most — providing great experiences for your guests.
    `,
    author: 'i-Rent Team',
    date: '2023-04-10',
    readTime: '2 min',
    category: 'Operations',
  },
  {
    slug: 'advanced-seo-editing-tool',
    title: 'Advanced SEO Editing Tool: Boost Your Website Rankings',
    excerpt: 'Take control of your search engine visibility with our advanced SEO editing tool — add custom meta tags, optimize keywords, and climb higher in Google search results.',
    content: `
## Why SEO Matters for Holiday Rentals

Search engine optimization is essential for driving organic traffic to your rental website. When potential guests search for accommodations in your area, you want your properties to appear at the top of the results. Our advanced SEO editing tool gives you the power to make that happen.

## How Keywords Improve Your Visibility

Search engines like Google evaluate the relevance of websites based on keyword usage. By strategically incorporating relevant keywords throughout your property descriptions and page content, you significantly increase your chances of ranking higher in search results.

For example, when someone searches for "beachfront villa in Marbella" or "family apartment Costa Blanca," having these terms naturally integrated into your content helps search engines match your listings to those queries.

![SEO editing interface](https://blog.i-rent.net/wp-content/uploads/2023/04/screenshot-edit-vpc.i-rent.net-2023.04.19-12_31_50-1024x404.png)

## Key Benefits

- **Higher search rankings** — Optimized content means better visibility on Google and other search engines
- **More organic traffic** — Reach guests who are actively searching for accommodations like yours
- **Increased bookings** — More visibility translates directly to more reservation inquiries
- **Full control** — Edit meta titles, descriptions, and keywords for each page

## How to Access the SEO Tool

Getting started is simple:

1. Activate the editors on your i-Rent website
2. Navigate to your accommodation list
3. Select either **default metas** or **custom metas** for each listing
4. Add your optimized titles, descriptions, and keywords

## Start Optimizing Today

Don't leave your search visibility to chance. Use our advanced SEO editing tool to ensure your properties are found by the right guests at the right time. Better SEO means better business.
    `,
    author: 'i-Rent Team',
    date: '2023-04-19',
    readTime: '2 min',
    category: 'Technology',
  },
  {
    slug: 'travelstaytion-ota-integration',
    title: 'Expand Your Reach: Connect to TravelStaytion via i-Rent',
    excerpt: 'Distribute your properties across 20+ leading B2B accommodation platforms worldwide with TravelStaytion — now available as an OTA connection in i-Rent.',
    content: `
## What Is TravelStaytion?

TravelStaytion is a curated network of quality holiday rental properties, powered by intelligent technology and a community of professional property owners. With access to over **90,000 properties** in more than **800 destinations** worldwide, they offer a unique value proposition for partners in the travel and hospitality industry.

![TravelStaytion logo and platform](https://blog.i-rent.net/wp-content/uploads/2023/05/Travelstaytion.png)

## Why Partner with TravelStaytion?

By connecting your properties to TravelStaytion through i-Rent, you unlock distribution across **more than 20 leading B2B accommodation platforms** around the globe. This means:

- **Wider audience reach** — Your listings appear on multiple high-traffic booking channels
- **Increased booking productivity** — More visibility translates to more reservations
- **Revenue growth** — Tap into new markets and traveler segments you couldn't reach before
- **Quality standards** — Join a network known for exceptional guest experiences

## How to Get Started

Connecting your properties is straightforward:

1. **Sign up with TravelStaytion** — [Complete their partner registration form](https://docs.google.com/forms/d/e/1FAIpQLSdtgWSUdP6sEOJqcU0LKqx_P1CaGprGajGy3Q8Inhx4tc5a3w/viewform)
2. **Contact i-Rent support** — Email soporte@i-rent.net to finalize the channel connection
3. **Start distributing** — Once connected, your availability and rates sync automatically

## Grow Your Business Today

Don't miss out on this opportunity to expand your distribution network and boost your bookings. TravelStaytion's expertise in the holiday rental industry, combined with i-Rent's seamless channel management, makes this partnership a win-win for property managers looking to scale.

[Learn more about TravelStaytion →](https://www.travelstaytion.com/)
    `,
    author: 'i-Rent Team',
    date: '2023-05-15',
    readTime: '2 min',
    category: 'Marketing',
  },
  {
    slug: 'unified-guest-messaging',
    title: 'New: Chat with Booking.com and Airbnb Guests Directly from i-Rent',
    excerpt: 'Communicate with all your OTA guests from one unified inbox — no more switching between Airbnb and Booking.com extranets.',
    content: `
## One Inbox for All Your Guests

We're thrilled to introduce a game-changing feature in i-Rent: **unified guest messaging**. You can now chat directly with guests who booked through Booking.com and Airbnb — all without leaving i-Rent.

No more logging into multiple extranets just to respond to a simple question. Our integrated messaging tool brings all your guest conversations into one central location.

## Why This Matters

Managing guest communication across different platforms is time-consuming and error-prone. Important messages get missed, response times suffer, and the guest experience takes a hit.

With unified messaging in i-Rent, you can:

- **Respond faster** — All messages arrive in one place, so you never miss a notification
- **Stay organized** — Keep all guest communication linked to their booking
- **Save time** — No more switching between browser tabs and mobile apps
- **Improve reviews** — Quick, consistent communication leads to happier guests

## How It Works

Getting started is incredibly simple:

1. Open i-Rent and look for the **Chat** button in the main menu
2. You'll see all incoming messages from Booking.com and Airbnb guests
3. Reply directly — your message is sent through the appropriate channel automatically

![Chat button location](https://blog.i-rent.net/wp-content/uploads/2023/06/screenshot-www.rentalbookingsystem.com-2023.06.13-12_39_56-1024x40.png)

![Unified messaging interface](https://blog.i-rent.net/wp-content/uploads/2023/06/screenshot-messenger.rentalbookingsystem.com-2023.06.13-12_47_34-1-fotor-2023061385528-1024x590.png)

## Experience the Convenience

Say goodbye to the hassle of juggling multiple platforms. With i-Rent's unified messaging, you'll enjoy seamless, stress-free communication with every guest — regardless of where they booked.

Try it today and discover how much easier guest management can be!
    `,
    author: 'i-Rent Team',
    date: '2023-06-13',
    readTime: '2 min',
    category: 'Operations',
  },
  {
    slug: 'booking-statistics-tool',
    title: 'New Tool: View Booking Statistics at a Glance',
    excerpt: 'Generate insightful charts and track key metrics like bookings, sources, net sales, and more — all from one powerful statistics dashboard.',
    content: `
## Introducing the Booking Statistics Tool

We're excited to announce a powerful new analytics feature in i-Rent! Accessing comprehensive statistics and generating visual charts has never been easier. With just a few clicks, you can gather insights and visualize your rental business data instantly.

## What Can You Track?

Our new statistics tool allows you to effortlessly select parameters and generate charts that provide valuable information:

- **Booking Volume**: Track how many bookings you're receiving over time
- **Booking Sources**: See which channels (Airbnb, Booking.com, direct, etc.) are driving your reservations
- **Creation Dates**: Understand when bookings are being made
- **Net & Gross Sales**: Monitor your revenue with detailed financial breakdowns
- **And Much More**: Customize charts to display the metrics that matter most to your business

## Key Features

### Customizable Charts
Generate charts tailored to your specific needs. Choose the metrics, date ranges, and visualization types that work best for you.

### Print & Export
Need a physical copy or want to share with your team? Simply print your charts or export them for presentations and reports.

### Favorites Dashboard
Mark your most important charts as favorites. These will be prominently displayed on your i-Rent homepage, giving you quick access to critical insights at a glance.

## How to Access

In i-Rent, navigate to **Administrator → Booking Statistics** to start exploring your data.

![Booking statistics dashboard](https://blog.i-rent.net/wp-content/uploads/2023/05/screenshot-master.rentalbookingsystem.com-2023.05.31-12_38_03-1-1024x488.png)

![Chart customization options](https://blog.i-rent.net/wp-content/uploads/2023/05/screenshot-master.rentalbookingsystem.com-2023.05.31-12_41_23-1024x450.png)

## Why This Matters

Data-driven decisions lead to better business outcomes. With the Booking Statistics tool, you can:

- Identify your best-performing channels and double down on what works
- Spot seasonal trends and adjust pricing accordingly
- Track revenue growth and set realistic targets
- Make informed decisions backed by real data

Discover a smarter way to manage your rentals with i-Rent!
    `,
    author: 'i-Rent Team',
    date: '2023-05-31',
    readTime: '3 min',
    category: 'Operations',
  },
  {
    slug: 'guest-reviews-now-available-to-owners',
    title: 'Guest Reviews Now Available to Property Owners',
    excerpt: 'Property owners can now view guest reviews directly in the owner portal, providing greater transparency and actionable insights to improve their accommodations.',
    content: `
## A New Level of Transparency

We're excited to announce a powerful new feature in the owner portal: property owners can now view guest reviews directly from their dashboard.

This update is designed to improve communication between property managers and owners by providing instant access to valuable guest feedback. Owners can now see exactly what guests appreciate and where there's room for improvement.

## Key Benefits

- **Actionable Insights**: Owners can analyze reviews to understand what guests value most about their property
- **Identify Opportunities**: Spot recurring themes or issues that need attention
- **Better Collaboration**: Facilitate meaningful discussions between managers and owners based on real feedback
- **Track Performance**: Monitor guest satisfaction over time

## How to Enable This Feature

To give an owner access to guest reviews, follow these steps:

1. In i-Rent, navigate to the **People** menu and select **Owners**
2. Click the pencil icon next to the owner's name to open their profile
3. Go to the **Login** tab where you manage permissions
4. Scroll down and enable: **Accommodation → Ratings**
5. Save your changes

![Owner permissions settings](/images/blog/guest-reviews-permissions.png)

## Questions?

For any questions about this feature, please contact us at [soporte@i-rent.net](mailto:soporte@i-rent.net).
    `,
    author: 'i-Rent Team',
    date: '2024-01-17',
    readTime: '2 min',
    category: 'Technology',
  },
  {
    slug: 'flexible-pricing-options-now-available-in-i-rent',
    title: 'Flexible Pricing Options Now Available in i-Rent',
    excerpt: 'Manage your rates, set stay durations, and adjust arrival and departure days easier than ever with our new flexible pricing features.',
    content: `
## More Flexibility and Convenience

To provide you with even more flexibility and convenience, we've optimized several helpful features in i-Rent. Now, managing your rates, setting stay durations, and adjusting arrival and departure days is easier than ever — all from a single, streamlined view!

## How to Set Up Flexible Pricing

1. Go to your accommodation list and open the information sheet of the relevant property
2. Click on the **Rate Management** tab and select **Change Rate Plan**
3. Choose **Flexible Pricing** and save your changes

![Setting up flexible pricing](https://t6635225.p.clickup-attachments.com/t6635225/a60e5edc-fbe7-4e51-a297-014f47e670ed/Dibujo%20(8).png)

**Coming soon:** Prices set per length of stay and per person.

## What Can You Do with Flexible Pricing?

- Set minimum and maximum stay durations
- Define flexible rate plans
- Manage arrival and departure days for smoother booking arrangements

In the extended menu of the accommodation (right-click on the accommodation name), go to **Pricing** and select the option you want to adjust from the top menu.

![Flexible pricing menu](https://t6635225.p.clickup-attachments.com/t6635225/292168e7-f588-4757-97e9-1cc156513548/Flexible%20pricing%20(1).png)

![Flexible pricing calendar](https://t6635225.p.clickup-attachments.com/t6635225/ba91a8d1-7a7c-4019-ba6d-d295f5cabb0a/Dibujo%20(6).png)

**Tip:** Need to select multiple days, weeks, or even months at once? Hold down the **Ctrl** key and use your mouse to click on the desired days, weeks or months.

## Experience the Freedom

Experience the freedom and simplicity of these new features, and make your pricing system as flexible as you like!

If you would like a brief introduction, feel free to let us know and we will schedule a Google Meet.
    `,
    author: 'i-Rent Team',
    date: '2024-12-10',
    readTime: '3 min',
    category: 'Technology',
  },
  {
    slug: 'new-ota-vacalia-available-in-i-rent',
    title: 'New OTA Available in i-Rent: Vacalia',
    excerpt: 'Expand your reach with Vacalia, a new OTA integration now available in i-Rent. Learn how to connect and start publishing your accommodations.',
    content: `
## Introducing Vacalia Integration

From now on, you can also publish your accommodations to Vacalia, a growing Online Travel Agency focused on holiday rentals.

## How to Get Started

Getting connected with Vacalia is straightforward:

1. **Contact Vacalia** - Reach out to info@vacalia.com to set up your contract with the OTA
2. **Add to i-Rent** - Once your contract is active, add Vacalia in i-Rent using the key: **VACAL**
3. **Start Publishing** - Your accommodations will be available on Vacalia's platform

## Why Add Another Channel?

Adding more distribution channels helps you:

- Reach new audiences and markets
- Increase your booking opportunities
- Reduce dependency on a single platform
- Maximize your property's visibility

## Need Help?

If you have any questions about the Vacalia integration or need assistance setting it up, please don't hesitate to contact our support team at soporte@i-rent.net.
    `,
    author: 'i-Rent Team',
    date: '2024-11-15',
    readTime: '2 min',
    category: 'Marketing',
  },
  {
    slug: 'go-to-button-quick-routes-accommodation',
    title: 'New Go-To Button for Quick Routes to the Accommodation',
    excerpt: 'Discover the new Go-To button feature that instantly generates directions to your accommodation, making it easy to share routes via Google Maps, WhatsApp, or email.',
    content: `
## Introducing the Go-To Button

You can now find the brand-new Go-To button in the accommodation window. This practical tool allows you to instantly generate directions from any starting point to the selected accommodation. Whether you start from your home, the airport, the train station or any other address, the Go-To button will create the quickest route for you.

![Go-To Button Route Planner](/images/blog/goto-button-route-planner.png)

## Share Routes Easily

Once generated, the button provides a link that you can easily:

- Open directly in Google Maps for step-by-step navigation
- Share instantly via WhatsApp with family, friends or guests
- Send by email to provide clear travel instructions
- Forward to any smartphone for use on the go

## Why This Matters

This makes giving and sharing directions to accommodations faster, easier and more reliable than typing in the address manually. It saves time, reduces errors and ensures that everyone arrives at the right location without confusion.

## Try It Today

We invite you to try out the new Go-To button and let us know your experience. Your feedback helps us improve and create even smarter tools for our users.
    `,
    author: 'i-Rent Team',
    date: '2025-01-28',
    readTime: '3 min',
    category: 'Operations',
  },
  {
    slug: 'maximize-occupancy-rates-holiday-rentals',
    title: 'How to Maximize Occupancy Rates for Your Holiday Rentals',
    excerpt: 'Learn proven strategies to keep your properties booked year-round, from dynamic pricing to seasonal promotions.',
    content: `
## Understanding Occupancy Rates

Occupancy rate is one of the most critical metrics for holiday rental success. It measures the percentage of time your property is booked versus available. A healthy occupancy rate typically ranges from 60-80%, depending on your market and property type.

## Strategy 1: Implement Dynamic Pricing

Dynamic pricing adjusts your rates based on demand, seasonality, local events, and competitor pricing. Properties using dynamic pricing see an average 10-20% increase in revenue.

**Tips for dynamic pricing:**
- Use automated pricing tools integrated with your PMS
- Set minimum and maximum rate limits
- Factor in local events and holidays
- Monitor competitor rates regularly

## Strategy 2: Optimize Your Listings

Your listing is your storefront. High-quality photos, compelling descriptions, and complete amenity lists significantly impact booking rates.

**Listing optimization checklist:**
- Professional photography with natural lighting
- Virtual tours or video walkthroughs
- Detailed, benefit-focused descriptions
- Complete and accurate amenity lists
- Quick response to inquiries

## Strategy 3: Multi-Channel Distribution

Don't rely on a single booking platform. Connect to multiple OTAs and maintain your own booking website to maximize visibility.

**Channel strategy:**
- List on major platforms (Booking.com, Airbnb, VRBO)
- Use regional platforms for local markets
- Build your direct booking website
- Use a channel manager for real-time sync

## Strategy 4: Seasonal Promotions

Create targeted promotions for low-demand periods. Last-minute discounts, extended stay offers, and seasonal packages can fill gaps in your calendar.

## Strategy 5: Guest Experience Excellence

Happy guests leave positive reviews, which drive future bookings. Focus on exceeding expectations from first inquiry to checkout.

**Guest experience tips:**
- Fast response times (under 1 hour)
- Personalized welcome messages
- Local recommendations and guidebooks
- Smooth check-in process
- Follow-up after checkout

## Measuring Success

Track your occupancy rate monthly and compare year-over-year. Aim for consistent improvement while maintaining your target ADR (Average Daily Rate).
    `,
    author: 'Carlos Fernández',
    date: '2024-01-15',
    readTime: '8 min',
    category: 'Revenue Management',
    featured: true,
  },
  {
    slug: 'channel-manager-benefits-property-managers',
    title: 'Why Every Property Manager Needs a Channel Manager',
    excerpt: 'Discover how channel managers eliminate double bookings, save time, and increase your property visibility across platforms.',
    content: `
## What is a Channel Manager?

A channel manager is software that synchronizes your property listings, availability, and pricing across multiple booking platforms in real-time. It's the bridge between your Property Management System (PMS) and the dozens of OTAs where your properties are listed.

## The Problem with Manual Management

Managing multiple platforms manually is time-consuming and error-prone:
- Hours spent updating calendars across platforms
- Risk of double bookings and costly cancellations
- Inconsistent pricing and information
- Delayed availability updates

## Key Benefits of a Channel Manager

### 1. Eliminate Double Bookings
Real-time synchronization means your availability updates instantly across all connected platforms. When a booking comes in on Airbnb, your Booking.com calendar updates within seconds.

### 2. Save 10+ Hours Per Week
No more logging into multiple platforms to update availability. Make changes once, and they propagate everywhere automatically.

### 3. Increase Visibility and Bookings
Connect to 50+ platforms and reach millions of potential guests. More visibility means more bookings.

### 4. Maintain Rate Parity
Set your base rates once and apply channel-specific adjustments. Ensure consistent pricing while accounting for different commission structures.

### 5. Centralized Management
View and manage all your channels from one dashboard. See where your bookings come from and optimize your channel mix.

## Choosing the Right Channel Manager

Look for these features:
- **Real-time sync**: Updates in seconds, not hours
- **Wide platform coverage**: Major OTAs plus regional platforms
- **PMS integration**: Seamless connection with your existing software
- **Commission-based pricing**: Pay only when you get bookings
- **Reliability**: High uptime and dedicated support

## ROI of a Channel Manager

The investment in a channel manager typically pays for itself within the first month through:
- Prevented double bookings (average cost: €200-500 per incident)
- Time savings (10+ hours/week)
- Increased bookings from expanded reach
- Better pricing optimization
    `,
    author: 'Anna Schmidt',
    date: '2024-01-08',
    readTime: '6 min',
    category: 'Technology',
    featured: true,
  },
  {
    slug: 'guest-communication-best-practices',
    title: 'Guest Communication Best Practices for Holiday Rentals',
    excerpt: 'Master the art of guest communication to boost reviews, reduce inquiries, and create memorable experiences.',
    content: `
## The Importance of Guest Communication

Effective communication is the foundation of great guest experiences. Properties with excellent communication consistently receive higher reviews and more repeat bookings.

## Pre-Booking Communication

### Quick Response Times
Respond to inquiries within 1 hour during business hours. Fast responses significantly increase conversion rates.

### Answer Questions Before They're Asked
Create detailed listings that address common questions about:
- Check-in/check-out times
- Parking availability
- Pet policies
- Nearby amenities

## Post-Booking Communication

### Booking Confirmation
Send an immediate confirmation with:
- Booking details and total cost
- Payment schedule
- Cancellation policy
- Your contact information

### Pre-Arrival Messages
1 week before: Send detailed check-in instructions
3 days before: Reminder with local weather and packing tips
1 day before: Final details and emergency contacts

### Check-In Day
Send a welcome message when guests arrive. Ask if they need anything and offer to help with questions.

## During Stay Communication

### Be Available but Not Intrusive
Make it easy for guests to reach you, but don't overwhelm them. A mid-stay check-in message on day 2-3 is appropriate for longer stays.

### Problem Resolution
When issues arise:
1. Respond immediately
2. Acknowledge the problem
3. Offer a solution or timeline
4. Follow up to ensure resolution

## Post-Stay Communication

### Thank You and Review Request
Send a thank-you message within 24 hours of checkout. Include a gentle request for a review.

### Future Booking Incentive
Offer returning guests a small discount for their next stay. Repeat guests are more profitable than new guests.

## Communication Tools

### Automated Templates
Create templates for common communications:
- Booking confirmations
- Check-in instructions
- Check-out reminders
- Review requests

### Multi-Language Support
If you host international guests, offer communication in their language. Translation tools or multilingual templates help bridge language gaps.

## Measuring Communication Success

Track these metrics:
- Average response time
- Guest review scores for communication
- Number of pre-stay inquiries (fewer is better if your info is comprehensive)
- Repeat booking rate
    `,
    author: 'Sophie Martin',
    date: '2024-01-02',
    readTime: '7 min',
    category: 'Guest Experience',
  },
  {
    slug: 'pricing-strategies-seasonal-rentals',
    title: 'Pricing Strategies for Seasonal Holiday Rentals',
    excerpt: 'Learn how to optimize your pricing throughout the year to maximize revenue during peak seasons and maintain bookings during slow periods.',
    content: `
## Understanding Seasonal Pricing

Holiday rental pricing isn't static. Demand fluctuates based on seasons, holidays, local events, and even days of the week. Smart pricing strategies can increase your annual revenue by 20-40%.

## Know Your Seasons

### Identify Your Market's Patterns
Every market has unique seasonal patterns:
- Beach properties: Summer peak, winter low
- Ski properties: Winter peak, summer low
- City properties: Event-driven peaks
- Year-round destinations: More stable demand

### Create a Seasonal Calendar
Map out your year:
- Peak season dates and rates
- Shoulder season dates and rates
- Low season dates and rates
- Special events and holidays

## Pricing Strategies by Season

### Peak Season
- Set premium rates (30-50% above base)
- Implement minimum stay requirements
- Require deposits and stricter cancellation policies
- Focus on maximizing revenue per booking

### Shoulder Season
- Moderate discounts (10-20% below peak)
- Flexible minimum stays
- Target last-minute bookers
- Promote weekday stays

### Low Season
- Significant discounts (20-40% below peak)
- Remove minimum stay requirements
- Offer extended stay discounts
- Create special packages

## Advanced Pricing Tactics

### Length of Stay Discounts
- 7+ nights: 10% discount
- 14+ nights: 15% discount
- 30+ nights: 20% discount

### Last-Minute Deals
Reduce rates for bookings within 7 days of arrival. Empty nights generate zero revenue.

### Early Bird Incentives
Offer small discounts for bookings made 3+ months in advance. This improves cash flow and predictability.

### Gap Night Pricing
If you have a 2-night gap between bookings, offer a special rate to fill it.

## Dynamic Pricing Tools

Consider using dynamic pricing software that:
- Monitors competitor rates
- Adjusts for demand signals
- Factors in local events
- Optimizes automatically

## Monitor and Adjust

Review your pricing monthly:
- Compare occupancy vs. competitors
- Analyze revenue per available night
- Test different strategies
- Adjust based on booking velocity
    `,
    author: 'Thomas van der Berg',
    date: '2023-12-20',
    readTime: '9 min',
    category: 'Revenue Management',
  },
  {
    slug: 'direct-bookings-reduce-ota-dependence',
    title: 'How to Increase Direct Bookings and Reduce OTA Dependence',
    excerpt: 'Build your own booking channel to save on commissions and create lasting guest relationships.',
    content: `
## The Case for Direct Bookings

While OTAs like Booking.com and Airbnb provide valuable exposure, their commission fees (15-20%) significantly impact your bottom line. Building a direct booking channel can save thousands annually and give you more control.

## Step 1: Create a Professional Website

Your website is your direct booking engine. It needs to:
- Look professional and trustworthy
- Show real-time availability
- Accept secure online payments
- Work perfectly on mobile devices
- Load quickly

### Essential Website Features
- High-quality photo galleries
- Detailed property descriptions
- Guest reviews and testimonials
- Clear pricing and booking calendar
- Easy-to-find contact information
- Secure payment processing

## Step 2: Optimize for Search Engines

SEO helps guests find you directly:
- Include location keywords in your content
- Create local area guides and content
- Optimize page titles and descriptions
- Build local citations and links
- Claim your Google Business Profile

## Step 3: Build an Email List

Collect guest emails (with permission) and nurture relationships:
- Send thank-you emails after stays
- Share local tips and updates
- Offer exclusive returning guest discounts
- Promote seasonal specials

## Step 4: Encourage Direct Rebooking

At checkout, give guests a reason to book direct next time:
- Business cards with your website
- Exclusive returning guest discount code
- Better cancellation terms for direct bookings

## Step 5: Leverage Social Media

Build a following and engage potential guests:
- Share beautiful property photos
- Highlight local attractions
- Post guest testimonials
- Run targeted ads to past website visitors

## Pricing for Direct Bookings

Consider offering slightly better rates for direct bookings:
- You save on OTA commissions (15-20%)
- Pass some savings to guests (5-10%)
- Keep the difference as extra profit

## Tracking Your Progress

Monitor these metrics:
- Percentage of direct vs. OTA bookings
- Website traffic and conversion rate
- Email list growth
- Returning guest rate
- Cost per acquisition by channel

## Realistic Expectations

Building a direct booking channel takes time. Aim for:
- Year 1: 10-15% direct bookings
- Year 2: 20-30% direct bookings
- Year 3+: 30-40% direct bookings
    `,
    author: 'Marco Rossi',
    date: '2023-12-15',
    readTime: '8 min',
    category: 'Marketing',
  },
  {
    slug: 'property-maintenance-holiday-rentals',
    title: 'Property Maintenance Best Practices for Holiday Rentals',
    excerpt: 'Keep your properties in top condition with preventive maintenance schedules and efficient turnover processes.',
    content: `
## Why Maintenance Matters

Well-maintained properties earn better reviews, command higher rates, and have fewer emergency issues. Neglected maintenance leads to costly repairs and unhappy guests.

## Preventive Maintenance Schedule

### Weekly Tasks
- HVAC filter check
- Smoke detector test
- Hot water temperature verification
- General cleanliness inspection

### Monthly Tasks
- Deep clean appliances
- Check for water leaks
- Inspect exterior for damage
- Test all electronics and remotes
- Clean gutters (seasonal)

### Quarterly Tasks
- HVAC system service
- Pest control treatment
- Mattress rotation
- Check fire extinguisher
- Inspect outdoor furniture

### Annual Tasks
- Full HVAC service
- Water heater flush
- Chimney cleaning (if applicable)
- Exterior paint touch-ups
- Major appliance inspection

## Turnover Maintenance Checklist

Between each guest:
- Full property cleaning
- Linen and towel change
- Amenity restock
- Quick damage inspection
- All lights and appliances working
- Fresh welcome amenities

## Building a Reliable Team

### Finding Quality Vendors
- Get referrals from other property managers
- Check reviews and references
- Verify insurance and licensing
- Test with small jobs first

### Key Team Members
- Cleaning crew (most important)
- Handyman for small repairs
- Plumber for emergencies
- Electrician for emergencies
- HVAC technician

### Backup Plans
Always have backup vendors for critical services. Emergencies don't wait for your primary vendor.

## Emergency Response

### Common Emergencies
- No hot water
- HVAC failure
- Plumbing issues
- Lock/key problems
- Power outages

### Response Protocol
1. Acknowledge guest concern immediately
2. Assess severity and safety
3. Deploy appropriate vendor
4. Keep guest updated on timeline
5. Follow up after resolution
6. Consider compensation if appropriate

## Technology for Maintenance

### Maintenance Management Tools
- Digital checklists for turnovers
- Scheduled maintenance reminders
- Vendor contact database
- Issue tracking and history
- Photo documentation

### Smart Home Devices
- Leak sensors for early detection
- Smart thermostats for HVAC monitoring
- Door sensors for security
- Noise monitors for party prevention

## Budgeting for Maintenance

Plan for:
- 1-2% of property value annually for maintenance
- Emergency fund (3-6 months of operating costs)
- Major replacement schedule (appliances, furniture)
    `,
    author: 'Carlos Fernández',
    date: '2023-12-08',
    readTime: '10 min',
    category: 'Operations',
  },
];

export const blogCategories = [
  'All',
  'Revenue Management',
  'Technology',
  'Guest Experience',
  'Marketing',
  'Operations',
];

export function getBlogPostBySlug(slug: string): BlogPost | undefined {
  return blogPosts.find((post) => post.slug === slug);
}

export function getFeaturedPosts(): BlogPost[] {
  return blogPosts.filter((post) => post.featured);
}
